import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { cn, formatCurrency, formatDate, formatDateOnly, formatInt } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader, BackLink } from '@/layouts/AppLayout';

// ── Types ──────────────────────────────────────────────────────────────────────

type BookingStatus =
  | 'draft'
  | 'tentative'
  | 'confirmed'
  | 'voucher_issued'
  | 'paid'
  | 'completed'
  | 'cancelled'
  | 'released';

interface BookingRoom {
  id: string;
  hotelRoomType: { id: string; name: string; maxOccupancy: number | null };
  mealPlan: string;
  occupancyType: string;
  guestsPerRoom: number;
  roomsCount: number;
  ratePerNight: string;
  rateSource: string;
  rateOverrideReason: string | null;
  contractedRateId: string | null;
  amount: string;
  notes: string | null;
  sortOrder: number;
}

interface Booking {
  id: string;
  bookingCode: string;
  hotelId: string;
  clientId: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
  nights: number | null;
  totalRoomNights: number;
  amount: string;
  confNo: string | null;
  bookerUserId: string;
  status: BookingStatus;
  tentativeUntil: string | null;
  cancellationReason: string | null;
  remark: string | null;
  createdAt: string;
  updatedAt: string | null;
  hotel: { id: string; name: string; city: string };
  client: { id: string; name: string; type: string };
  bookerUser: { id: string; fullName: string } | null;
  bookingRooms: BookingRoom[];
}

interface HistoryEntry {
  id: string;
  fromStatus: BookingStatus | null;
  toStatus: BookingStatus;
  changedAt: string;
  reason: string | null;
  changedBy: { id: string; fullName: string } | null;
}

interface TransitionPayload {
  toStatus: BookingStatus;
  reason?: string;
  cancellationReason?: string;
  tentativeUntil?: string;
  confNo?: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<BookingStatus, string> = {
  draft: 'Draft',
  tentative: 'Tentative',
  confirmed: 'Confirmed',
  voucher_issued: 'Voucher Issued',
  paid: 'Paid',
  completed: 'Completed',
  released: 'Released',
  cancelled: 'Cancelled',
};

// Mirrors §7.2 state machine — used only to render action buttons.
const TRANSITIONS: Record<BookingStatus, BookingStatus[]> = {
  draft: ['tentative', 'confirmed', 'cancelled'],
  tentative: ['confirmed', 'released', 'cancelled'],
  confirmed: ['voucher_issued', 'cancelled'],
  voucher_issued: ['paid', 'cancelled'],
  paid: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
  released: [],
};

type TransitionMeta = {
  label: string;
  variant: 'primary' | 'outline' | 'destructive';
  requiresModal: boolean;
};

const TRANSITION_META: Record<BookingStatus, TransitionMeta> = {
  tentative:      { label: 'Mark Tentative',    variant: 'outline',      requiresModal: true },
  confirmed:      { label: 'Confirm',           variant: 'primary',      requiresModal: true },
  voucher_issued: { label: 'Issue Voucher',     variant: 'primary',      requiresModal: false },
  paid:           { label: 'Mark Paid',         variant: 'primary',      requiresModal: false },
  completed:      { label: 'Mark Completed',    variant: 'outline',      requiresModal: false },
  released:       { label: 'Release',           variant: 'outline',      requiresModal: true },
  cancelled:      { label: 'Cancel Booking',    variant: 'destructive',  requiresModal: true },
  draft:          { label: 'Draft',             variant: 'outline',      requiresModal: false },
};

const STATUS_DOT: Record<BookingStatus, string> = {
  draft:          'bg-neutral-400',
  tentative:      'bg-warning-600',
  confirmed:      'bg-info-600',
  voucher_issued: 'bg-info-600',
  paid:           'bg-success-600',
  completed:      'bg-neutral-600',
  released:       'bg-neutral-400',
  cancelled:      'bg-danger-600',
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-2.5 border-b border-neutral-100 last:border-0">
      <span className="w-40 shrink-0 text-sm text-neutral-500">{label}</span>
      <span className="text-sm text-neutral-900">{value ?? '—'}</span>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white">
      <div className="px-4 py-3 border-b border-neutral-100">
        <h3 className="text-sm font-semibold text-neutral-700">{title}</h3>
      </div>
      <div className="px-4 py-1">{children}</div>
    </div>
  );
}

// ── Transition modal ───────────────────────────────────────────────────────────

interface ModalState {
  toStatus: BookingStatus;
  tentativeUntil: string;
  confNo: string;
  cancellationReason: string;
  reason: string;
}

function TransitionModal({
  state,
  onClose,
  onSubmit,
  isPending,
}: {
  state: ModalState;
  onClose: () => void;
  onSubmit: (payload: TransitionPayload) => void;
  isPending: boolean;
}) {
  const [tentativeUntil, setTentativeUntil] = useState(state.tentativeUntil);
  const [confNo, setConfNo] = useState(state.confNo);
  const [cancellationReason, setCancellationReason] = useState(state.cancellationReason);
  const [reason, setReason] = useState(state.reason);
  const [error, setError] = useState('');

  const toStatus = state.toStatus;
  const isDestructive = toStatus === 'cancelled' || toStatus === 'released';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (toStatus === 'tentative' && !tentativeUntil) {
      setError('Tentative-until date is required.');
      return;
    }
    if (toStatus === 'cancelled' && !cancellationReason.trim()) {
      setError('Cancellation reason is required.');
      return;
    }

    const payload: TransitionPayload = { toStatus };
    if (tentativeUntil) payload.tentativeUntil = tentativeUntil;
    if (confNo.trim()) payload.confNo = confNo.trim();
    if (cancellationReason.trim()) payload.cancellationReason = cancellationReason.trim();
    if (reason.trim()) payload.reason = reason.trim();

    onSubmit(payload);
  }

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
      <Dialog.Content
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-neutral-200 bg-white p-6 shadow-xl focus:outline-none"
      >
        <Dialog.Title className="text-base font-semibold text-neutral-900 mb-1">
          {STATUS_LABELS[toStatus]}
        </Dialog.Title>
        <Dialog.Description className="text-sm text-neutral-500 mb-5">
          {toStatus === 'tentative' && 'Set a date until which this booking is on hold.'}
          {toStatus === 'confirmed' && 'Optionally record the hotel confirmation number.'}
          {toStatus === 'released' && 'Release this tentative booking back to inventory.'}
          {toStatus === 'cancelled' && 'This action cannot be undone. A reason is required.'}
        </Dialog.Description>

        <form onSubmit={handleSubmit} className="space-y-4">
          {toStatus === 'tentative' && (
            <div className="space-y-1.5">
              <label htmlFor="tentative-until" className="text-sm font-medium text-neutral-700">
                Tentative until <span className="text-danger-700">*</span>
              </label>
              <Input
                id="tentative-until"
                type="date"
                value={tentativeUntil}
                onChange={(e) => setTentativeUntil(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
              />
            </div>
          )}

          {toStatus === 'confirmed' && (
            <div className="space-y-1.5">
              <label htmlFor="conf-no" className="text-sm font-medium text-neutral-700">Confirmation number</label>
              <Input
                id="conf-no"
                placeholder="Hotel conf. no. (optional)"
                value={confNo}
                onChange={(e) => setConfNo(e.target.value)}
              />
            </div>
          )}

          {toStatus === 'cancelled' && (
            <div className="space-y-1.5">
              <label htmlFor="cancellation-reason" className="text-sm font-medium text-neutral-700">
                Cancellation reason <span className="text-danger-700">*</span>
              </label>
              <Textarea
                id="cancellation-reason"
                rows={3}
                placeholder="Reason for cancellation…"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
              />
            </div>
          )}

          {(toStatus === 'released' || toStatus === 'tentative' || toStatus === 'confirmed') && (
            <div className="space-y-1.5">
              <label htmlFor="transition-note" className="text-sm font-medium text-neutral-700">Note (optional)</label>
              <Textarea
                id="transition-note"
                rows={2}
                placeholder="Any additional note…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          )}

          {error && <p className="text-sm text-danger-700">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={isDestructive ? 'destructive' : 'primary'}
              disabled={isPending}
            >
              {isPending ? <Spinner className="h-4 w-4" /> : STATUS_LABELS[toStatus]}
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Portal>
  );
}

// ── Status history timeline ────────────────────────────────────────────────────

function StatusTimeline({ entries }: { entries: HistoryEntry[] }) {
  if (!entries.length) {
    return <p className="text-sm text-neutral-400 py-3">No history yet.</p>;
  }

  return (
    <ol className="relative" aria-label="Status history">
      {entries.map((entry, i) => (
        <li key={entry.id} className="flex gap-3 pb-4 last:pb-0">
          {/* Dot + line */}
          <div className="relative flex flex-col items-center" aria-hidden="true">
            <span
              className={cn(
                'mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white',
                STATUS_DOT[entry.toStatus] ?? 'bg-neutral-400',
              )}
            />
            {i < entries.length - 1 && (
              <div className="mt-1 w-px flex-1 bg-neutral-200" />
            )}
          </div>

          {/* Content */}
          <div className="min-w-0 pb-1">
            <div className="flex items-center gap-2 flex-wrap">
              {entry.fromStatus ? (
                <span className="text-sm text-neutral-500">
                  <Badge variant={entry.fromStatus}>{STATUS_LABELS[entry.fromStatus]}</Badge>
                  {' → '}
                  <Badge variant={entry.toStatus}>{STATUS_LABELS[entry.toStatus]}</Badge>
                </span>
              ) : (
                <Badge variant={entry.toStatus}>{STATUS_LABELS[entry.toStatus]}</Badge>
              )}
            </div>
            <p className="mt-0.5 text-xs text-neutral-500">
              {entry.changedBy?.fullName ?? 'System'} · {formatDate(entry.changedAt)}
            </p>
            {entry.reason && (
              <p className="mt-0.5 text-xs text-neutral-600 italic">"{entry.reason}"</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [pendingTransition, setPendingTransition] = useState<ModalState | null>(null);

  const { data: booking, isLoading } = useQuery({
    queryKey: ['bookings', id],
    queryFn: () => api.get<Booking>(`/bookings/${id}`),
    enabled: !!id,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['bookings', id, 'history'],
    queryFn: () => api.get<HistoryEntry[]>(`/bookings/${id}/history`),
    enabled: !!id,
  });

  const transitionMutation = useMutation({
    mutationFn: (payload: TransitionPayload) =>
      api.post<Booking>(`/bookings/${id}/transition`, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(['bookings', id], updated);
      queryClient.invalidateQueries({ queryKey: ['bookings', id, 'history'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toast.success(`Booking moved to ${STATUS_LABELS[updated.status]}`);
      setPendingTransition(null);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Transition failed');
    },
  });

  function startTransition(toStatus: BookingStatus) {
    const meta = TRANSITION_META[toStatus];
    if (!meta.requiresModal) {
      transitionMutation.mutate({ toStatus });
      return;
    }
    setPendingTransition({
      toStatus,
      tentativeUntil: '',
      confNo: '',
      cancellationReason: '',
      reason: '',
    });
  }

  if (isLoading) {
    return <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>;
  }
  if (!booking) {
    return <p className="p-6 text-danger-700">Booking not found.</p>;
  }

  const allowedTransitions = TRANSITIONS[booking.status];

  return (
    <Dialog.Root open={pendingTransition !== null} onOpenChange={(open) => { if (!open) setPendingTransition(null); }}>
      <div>
        <PageHeader
          title={booking.bookingCode}
          description={`${booking.hotel.name} · ${booking.guestName}`}
          action={
            <Button asChild variant="outline" size="md">
              <Link to={`/bookings/${id}/edit`}>
                <Pencil className="h-4 w-4" />
                Edit
              </Link>
            </Button>
          }
        />

        <div className="p-6">
          <BackLink to="/bookings" label="Back to bookings" />

          <div className="grid gap-5 md:grid-cols-[1fr_320px]">
            {/* ── Left column ───────────────────────────────────────── */}
            <div className="space-y-4">
              <SectionCard title="Stay details">
                <InfoRow label="Hotel" value={
                  <Link to={`/hotels/${booking.hotel.id}`} className="text-tangerine-600 hover:underline">
                    {booking.hotel.name}
                  </Link>
                } />
                <InfoRow label="City" value={booking.hotel.city} />
                <InfoRow label="Guest name" value={booking.guestName} />
                <InfoRow label="Check-in" value={formatDateOnly(booking.checkIn)} />
                <InfoRow label="Check-out" value={formatDateOnly(booking.checkOut)} />
                <InfoRow label="Nights" value={booking.nights != null ? formatInt(booking.nights) : '—'} />
                <InfoRow
                  label="Total rooms"
                  value={formatInt(booking.bookingRooms.reduce((s, r) => s + r.roomsCount, 0))}
                />
                <InfoRow label="Total room-nights" value={formatInt(booking.totalRoomNights)} />
                <InfoRow
                  label="Total amount"
                  value={<span className="font-semibold">{formatCurrency(booking.amount)}</span>}
                />
              </SectionCard>

              {/* ── Per-room cards ─────────────────────────────────────── */}
              {booking.bookingRooms.map((room, i) => {
                const isAdhoc = room.rateSource === 'adhoc';
                return (
                  <div key={room.id} className="rounded-lg border border-neutral-200 bg-white">
                    <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
                      <h3 className="text-sm font-semibold text-neutral-700">
                        Room {i + 1} — {room.hotelRoomType.name}
                      </h3>
                      {isAdhoc ? (
                        <span className="inline-flex items-center rounded-full bg-[#FFF3E8] px-2 py-0.5 text-xs font-medium text-[#F57C1F]">
                          Ad-hoc rate
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-success-50 px-2 py-0.5 text-xs font-medium text-success-700">
                          Contract rate
                        </span>
                      )}
                    </div>
                    <div className="px-4 py-1">
                      <InfoRow label="Meal plan" value={room.mealPlan.toUpperCase()} />
                      <InfoRow label="Occupancy" value={<span className="capitalize">{room.occupancyType}</span>} />
                      <InfoRow label="Guests / room" value={room.guestsPerRoom} />
                      <InfoRow label="Rooms" value={room.roomsCount} />
                      <InfoRow label="Rate / night" value={formatCurrency(room.ratePerNight)} />
                      <InfoRow
                        label="Amount"
                        value={<span className="font-semibold">{formatCurrency(room.amount)}</span>}
                      />
                      {isAdhoc && room.rateOverrideReason && (
                        <InfoRow label="Override reason" value={room.rateOverrideReason} />
                      )}
                      {room.notes && <InfoRow label="Notes" value={room.notes} />}
                    </div>
                  </div>
                );
              })}

              <SectionCard title="Booking info">
                <InfoRow label="Client" value={
                  <Link to={`/clients/${booking.client.id}`} className="text-tangerine-600 hover:underline">
                    {booking.client.name}
                  </Link>
                } />
                <InfoRow label="Booker" value={booking.bookerUser?.fullName ?? '—'} />
                <InfoRow label="Conf. number" value={booking.confNo ?? '—'} />
                {booking.status === 'tentative' && booking.tentativeUntil && (
                  <InfoRow label="Tentative until" value={formatDateOnly(booking.tentativeUntil)} />
                )}
                {booking.cancellationReason && (
                  <InfoRow label="Cancellation reason" value={booking.cancellationReason} />
                )}
                {booking.remark && (
                  <InfoRow label="Remark" value={booking.remark} />
                )}
                <InfoRow label="Created" value={formatDate(booking.createdAt)} />
                {booking.updatedAt && (
                  <InfoRow label="Updated" value={formatDate(booking.updatedAt)} />
                )}
              </SectionCard>
            </div>

            {/* ── Right column ──────────────────────────────────────── */}
            <div className="space-y-4">
              {/* Status & actions */}
              <div className="rounded-lg border border-neutral-200 bg-white">
                <div className="px-4 py-3 border-b border-neutral-100">
                  <h3 className="text-sm font-semibold text-neutral-700">Status</h3>
                </div>
                <div className="px-4 py-4 space-y-3">
                  <Badge variant={booking.status} className="text-sm px-3 py-1">
                    {STATUS_LABELS[booking.status]}
                  </Badge>

                  {allowedTransitions.length > 0 && (
                    <div className="space-y-2 pt-1">
                      {allowedTransitions.map((toStatus) => {
                        const meta = TRANSITION_META[toStatus];
                        return (
                          <Button
                            key={toStatus}
                            variant={meta.variant}
                            size="sm"
                            className="w-full justify-center"
                            disabled={transitionMutation.isPending}
                            onClick={() => startTransition(toStatus)}
                          >
                            {transitionMutation.isPending &&
                            transitionMutation.variables?.toStatus === toStatus ? (
                              <Spinner className="h-3.5 w-3.5" />
                            ) : null}
                            {meta.label}
                          </Button>
                        );
                      })}
                    </div>
                  )}

                  {allowedTransitions.length === 0 && (
                    <p className="text-xs text-neutral-400">This booking is in a terminal state.</p>
                  )}
                </div>
              </div>

              {/* Status history */}
              <div className="rounded-lg border border-neutral-200 bg-white">
                <div className="px-4 py-3 border-b border-neutral-100">
                  <h3 className="text-sm font-semibold text-neutral-700">Status history</h3>
                </div>
                <div className="px-4 py-4">
                  <StatusTimeline entries={history} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {pendingTransition && (
        <TransitionModal
          state={pendingTransition}
          onClose={() => setPendingTransition(null)}
          onSubmit={(payload) => transitionMutation.mutate(payload)}
          isPending={transitionMutation.isPending}
        />
      )}
    </Dialog.Root>
  );
}
